import { useEffect, useState } from "react";
import { fetchProductInventory, inventoryAsText, type ProductInventory } from "../../api/product-inventory-client";
import "./creator-inventory.css";

export function CreatorInventoryPanel({ sessionToken }: { sessionToken: string }) {
  const [data, setData] = useState<ProductInventory>();
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<"inventory" | "one-core">("inventory");

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    const timer = window.setTimeout(() => controller.abort(), 15_000);
    setData(undefined);
    setError("");
    fetchProductInventory(sessionToken, controller.signal)
      .then((result) => { if (!disposed) setData(result); })
      .catch((reason: unknown) => {
        if (!disposed) setError(controller.signal.aborted
          ? "La consulta ha tardado demasiado. Vuelve a intentarlo."
          : reason instanceof Error ? reason.message : "No se pudo cargar el inventario.");
      })
      .finally(() => window.clearTimeout(timer));
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [sessionToken, attempt]);

  const isOneCore = selectedDocument === "one-core" && Boolean(data?.oneCore);
  const sections = isOneCore ? data?.oneCore?.sections : data?.sections;
  const statuses = isOneCore ? { DEFINIDO: "Requisito de producto; el estado de implementación se consulta por separado." } : data?.statuses ?? {};
  const normalized = query.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");
  const visible = sections?.filter((section) => {
    const haystack = `${section.id} ${section.title} ${section.content}`
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");
    return (!normalized || haystack.includes(normalized)) &&
      (!status || section.sourceStatus.includes(status));
  }) ?? [];

  function downloadInventory() {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([isOneCore && data.oneCore ? data.oneCore.sourceText : inventoryAsText(data)], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = isOneCore ? "OMNISYNIA_Plan_ONE_CORE_11-09-2026.txt" : "OMNISYNIA_Inventario_Maestro_11-09-2026.txt";
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return (
    <section className="account-card creator-inventory" aria-labelledby="creator-inventory-title">
      <div className="account-card-heading">
        <div>
          <span className="eyebrow">ÁREA PRIVADA DEL CREADOR</span>
          <h3 id="creator-inventory-title">Documentos del proyecto OMNISYNIA</h3>
        </div>
        {data && <button className="secondary-button" type="button" onClick={downloadInventory}>Descargar TXT</button>}
      </div>
      {!data && !error && <p role="status">Cargando inventario…</p>}
      {error && <div role="alert"><p>{error}</p><button className="secondary-button" type="button" onClick={() => setAttempt((value) => value + 1)}>Reintentar</button></div>}
      {data && <>
        <label htmlFor="project-document">Documento
          <select id="project-document" value={selectedDocument} onChange={(event) => {
            setSelectedDocument(event.target.value as "inventory" | "one-core");
            setStatus("");
          }}>
            <option value="inventory">Inventario maestro · 60 apartados</option>
            {data.oneCore && <option value="one-core">Plan ONE CORE · {data.oneCore.sections.length} apartados</option>}
          </select>
        </label>
        {isOneCore && data.oneCore
          ? <p>{data.oneCore.sections.length} apartados · Versión documental {data.oneCore.document.revision} · {data.oneCore.document.sourceDate}</p>
          : <p>{data.summary.sectionCount} apartados · {data.summary.incidentCount} incidencias documentadas · Corte: {data.document.cutoffDate}</p>}
        {data.oneCore && <details className="inventory-legend">
          <summary>Decisiones vigentes y avance de incorporación</summary>
          <p>El plan ONE CORE actualiza las decisiones siguientes. El inventario conserva el historial.</p>
          {data.oneCore.decisions.map((decision) => <div key={decision.id}>
            <p><strong>{decision.references.join(" → ")}</strong></p>
            <p>{decision.current}</p><p className="inventory-note">{decision.delivery}</p>
          </div>)}
          {data.oneCore.implementation.map((step) => <div key={step.id}>
            <p><strong>{step.title} · {step.status}</strong></p>
            <p>{step.detail}</p><p>Pendiente: {step.pending}</p>
          </div>)}
          <p className="inventory-note">La existencia de pruebas en el repositorio no acredita un despliegue ni una validación en dispositivos reales.</p>
        </details>}
        {isOneCore && data.oneCore && <p className="inventory-note">{data.oneCore.document.scope} {data.oneCore.document.transcription}</p>}
        <p className="inventory-note">{isOneCore ? "Requisitos de ONE CORE. Consulta el avance de incorporación para conocer el alcance del código." : data.verification.note}</p>
        <div className="inventory-controls">
          <label htmlFor="inventory-search">Buscar en el documento
            <input id="inventory-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Voz, imágenes, agentes…" />
          </label>
          <label htmlFor="inventory-status">Estado documental
            <select id="inventory-status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Todos los estados</option>
              {Object.keys(statuses).map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
        </div>
        <details className="inventory-legend">
          <summary>Qué significa cada estado</summary>
          <dl>{Object.entries(statuses).map(([name, description]) => <div key={name}><dt>{name}</dt><dd>{description}</dd></div>)}</dl>
        </details>
        <p aria-live="polite">{visible.length} apartados encontrados</p>
        <div className="inventory-sections">
          {visible.map((section) => <details className="inventory-section" key={section.id}>
            <summary><span>{section.id}. {section.title}</span><small>{section.sourceStatus}</small></summary>
            <div className="inventory-section-content">{section.content}</div>
          </details>)}
        </div>
        {visible.length === 0 && <p>No hay apartados que coincidan con la búsqueda.</p>}
        {data.document.pendingSource && <p className="inventory-note">{data.document.pendingSource}</p>}
      </>}
    </section>
  );
}
